// Composable to delete the current user from Firestore and Auth
import { auth, db } from "./index";
import { deleteUser } from "firebase/auth";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  limit,
  getDoc,
} from "firebase/firestore";

/**
 * Deletes Firestore user doc (and subcollections) then deletes Auth user.
 * Returns { ok: true } on success, or { ok: false, code, message } on failure.
 */
export async function deleteCurrentUserAccount() {
  const user = auth.currentUser;
  if (!user) {
    return { ok: false, code: "no-user", message: "No authenticated user." };
  }

  const uid = user.uid;

  try {
    // 0) Prevent deleting the last admin
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    const isAdmin = userSnap.exists() && userSnap.data()?.privileges === "admin";
    if (isAdmin) {
      const adminsQ = query(
        collection(db, "users"),
        where("privileges", "==", "admin"),
        limit(2)
      );
      const adminsSnap = await getDocs(adminsQ);
      // If there is only 1 admin (this user), block deletion
      if (adminsSnap.size <= 1) {
        return {
          ok: false,
          code: "last-admin",
          message: "Cannot delete the last admin account.",
        };
      }
    }

    // 1) Delete subcollection 'content_requests'
    const reqCol = collection(db, "users", uid, "content_requests");
    const reqSnap = await getDocs(reqCol);
    const deletes = [];
    reqSnap.forEach((d) => {
      deletes.push(deleteDoc(doc(db, "users", uid, "content_requests", d.id)));
    });
    if (deletes.length) await Promise.all(deletes);

    // 2) Delete user document
    await deleteDoc(userRef);

    // 3) Delete Auth user (may require recent login)
    await deleteUser(user);

    return { ok: true };
  } catch (err) {
    const code = err?.code || "unknown";
    // Common case: auth/requires-recent-login
    return { ok: false, code, message: err?.message || String(err) };
  }
}

/**
 * Admin-only: deletes the specified user by UID via Cloud Function.
 * Performs Auth deletion and Firestore cleanup on the server.
 * Returns { ok: true } on success, or { ok: false, code, message } on error.
 */
export async function adminDeleteUserByUid(targetUid) {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { ok: false, code: "no-user", message: "Not authenticated" };
    }
    const idToken = await user.getIdToken();

    // Choose endpoint depending on environment (emulator vs production)
    // Check for localhost, 127.0.0.1, or any local IP (192.168.x.x, etc.)
    const isLocal = typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
       window.location.hostname === "127.0.0.1" ||
       window.location.hostname.startsWith("192.168.") ||
       window.location.hostname.startsWith("10.") ||
       window.location.port === "8080"); // dev server port

    const projectId = "the33path"; // from firebase config
    const url = isLocal
      ? `http://127.0.0.1:5001/${projectId}/us-central1/adminDeleteUser`
      : `https://us-central1-${projectId}.cloudfunctions.net/adminDeleteUser`;

    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ uid: targetUid }),
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      // Map known codes if present
      return {
        ok: false,
        code: data && data.code ? data.code : `http-${resp.status}`,
        message: (data && (data.error || data.message)) || "Request failed",
      };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, code: "unknown", message: err && err.message ? err.message : String(err) };
  }
}
